/**
 * Binding-attestation wire contract for the WayAI → Rekor service binding.
 *
 * Identity rides these
 * headers and nothing else — the binding forwards NO credential, and the
 * Rekor side trusts them because they arrived on an entrypoint unreachable from
 * outside the Cloudflare account, not because it verified their contents.
 *
 * Two header namespaces meet here and must not be confused:
 *
 * - `X-Rekor-*` is OUTBOUND-ONLY, server-derived, and never integrator-facing.
 *   WayAI mints every one of these from `AuthContext`; an inbound copy is discarded.
 * - `X-WayAI-*` is what WayAI's OWN clients (browser, CLI, MCP) may send. Both of the
 *   inbound ones below are advisory audit provenance — a spoof mislabels an audit row
 *   and grants nothing — which is why they are the only client-settable inputs at this
 *   seam. `X-Rekor-Active-Org`, the client-settable org *hint*, is deliberately retired
 *   — org selection is validated WayAI-side and only the validated result is
 *   attested.
 */

import { z } from 'zod';

// =============================================
// Outbound attestation headers (WayAI → Rekor)
// =============================================

/** `user` | `token` — mirrors Rekor's own branch on auth kind. */
export const REKOR_ACTOR_TYPE_HEADER = 'X-Rekor-Actor-Type';
/** `auth.user_id` (user) or `auth.token_id` (token). */
export const REKOR_ACTOR_ID_HEADER = 'X-Rekor-Actor-Id';
/** Human-readable audit label. Required to be present; may be empty. */
export const REKOR_ACTOR_LABEL_HEADER = 'X-Rekor-Actor-Label';
/** The grant-validated WayAI org UUID. New in step 1; replaces `X-Rekor-Active-Org`. */
export const REKOR_ACTOR_ORG_HEADER = 'X-Rekor-Actor-Org';
/**
 * `1` or `0`, ALWAYS sent. Absence-means-false would make a broken header pipeline
 * indistinguishable from "not an admin" — a silent privilege downgrade instead of the
 * 401 the attested entrypoint owes a malformed attestation.
 */
export const REKOR_ACTOR_ADMIN_HEADER = 'X-Rekor-Actor-Admin';
/** `api` | `cli` | `mcp` — audit `source`, constrained to Rekor's existing allowlist. */
export const REKOR_CLIENT_HEADER = 'X-Rekor-Client';
/** Action provenance, forwarded from the WayAI client. Unchanged from Rekor's own use. */
export const REKOR_TOOL_ID_HEADER = 'X-Rekor-Tool-Id';

// =============================================
// Inbound client hint (WayAI's own clients → WayAI)
// =============================================

/**
 * Action provenance from the WayAI client, mapped onto `X-Rekor-Tool-Id`. Named
 * `X-WayAI-*` rather than `X-Rekor-*` because the Rekor spelling is
 * internal-only: the WayAI `/api` surface is what the CLI and MCP author against.
 *
 * This is the ONLY client-settable value the attestation carries, carved out by name:
 * a spoofed tool id mislabels one audit row and grants nothing (what Rekor drops is stated
 * at `resolveToolId` in `apps/backend/src/rekor/attestation.ts`). No such carve-out exists
 * for the client/`source` header — see `REKOR_CLIENTS`.
 */
export const WAYAI_TOOL_ID_HEADER = 'X-WayAI-Tool-Id';

/**
 * Rekor's audit `source` allowlist.
 *
 * WayAI attests `api` and only `api`. `cli` and `mcp` map to their own surfaces, but
 * WayAI has nothing server-derived to distinguish them: browser, CLI, and MCP all reach
 * `/api/*` with the same credential shapes, so the only available discriminator would be
 * a header the caller sets — which makes the audit `source` a value the request author
 * chooses. A uniformly-`api` label is honestly uninformative; a settable one is actively
 * misleading to whoever reads the audit log during an incident. The other two land when a
 * server-derived marker exists to carry them (the OAuth client id is the natural
 * candidate — the CLI and MCP use different WorkOS apps).
 */
export const REKOR_CLIENTS = ['api', 'cli', 'mcp'] as const;
export type RekorClient = (typeof REKOR_CLIENTS)[number];

export const rekorClientSchema = z.enum(REKOR_CLIENTS);

/** The value WayAI attests today — see `REKOR_CLIENTS`. */
export const WAYAI_ATTESTED_CLIENT: RekorClient = 'api';

/**
 * Rekor caps the accepted provenance header at 128 chars so a raw-REST caller can't
 * bloat its audit log. WayAI forwards subject to the same rules, so an
 * over-long value is dropped here rather than shipped for Rekor to drop.
 */
export const MAX_REKOR_TOOL_ID_LENGTH = 128;

/**
 * Bounds on the forwarded provenance value. Declared once and reused by both the
 * attestation shape below and the builder that drops an out-of-bounds value, so the two
 * cannot disagree after a tightening on either side.
 */
export const rekorToolIdSchema = z.string().trim().min(1).max(MAX_REKOR_TOOL_ID_LENGTH);

// =============================================
// The `/api/data` proxy surface
// =============================================

// `DATA_PROXY_MOUNT` and `DATA_PROXY_PREFIX` are declared in `./route-paths` (it says why).

/**
 * Everything under the prefix is forwarded beneath `/v1` on the data API's
 * `AttestedEntrypoint` and nowhere else. Rekor's non-`/v1` surfaces (`/s3`, `/executor`, `/inbound`, `/admin`,
 * `/webhooks`) authenticate on their own terms and must stay unreachable through an
 * attested hop.
 */
export const REKOR_V1_PREFIX = '/v1';

/**
 * The request headers a File upload carries through the proxy: its version check (the
 * `ETag` a read returned) and the metadata and label of the version it writes. Both WayAI
 * hops forward exactly these — the data proxy and the web proxy in front of it — and
 * dropped, the upload still succeeds: unconditionally, without its metadata and label.
 *
 * Cross-repo wire literals: the far side spells
 * the last two `DATA_METADATA_HEADER` and `DATA_LABEL_HEADER` in
 * `apps/rekor/shared/src/constants.ts`. Lowercase, as both hops' allowlists spell names.
 */
export const DATA_FILE_UPLOAD_HEADERS = ['if-match', 'x-data-metadata', 'x-data-label'] as const;

/**
 * The binding-only lifecycle surface — today one operation, the mark-for-erasure that
 * ends a two-phase production hard delete.
 *
 * It sits OUTSIDE `/v1` precisely so `resolveRekorUpstreamUrl` cannot forward to it: the
 * proxy attests any `org:write` caller, including a `way_` agent token, and this operation
 * must only ever be issued by WayAI's own grace timer after a human opened an emailed
 * confirmation link. Reaching it means building the attestation and calling
 * `env.REKOR.fetch` directly — see `apps/backend/src/rekor/base-erasure.ts`.
 *
 * A cross-repo wire literal, like the `X-Rekor-*` header names above: the far side spells it
 * as `LIFECYCLE_PREFIX` in `apps/rekor/backend/src/router.ts`, and the two must not drift.
 */
export const REKOR_LIFECYCLE_PREFIX = '/lifecycle';

/** The one operation on that surface. `base_id` is path-encoded by the caller. */
export function rekorBaseErasurePath(baseId: string): string {
  return `${REKOR_LIFECYCLE_PREFIX}/bases/${encodeURIComponent(baseId)}/erasure`;
}

/**
 * Origin for the binding fetch. Hono routes by path and the host is ignored, so this is
 * cosmetic — but a service-binding fetch still needs an absolute URL, and an
 * internal-looking one keeps a stray log line from reading as a public call.
 */
export const REKOR_BINDING_ORIGIN = 'https://rekor-backend.internal';

/**
 * Org selector for the proxy. `X-Rekor-Active-Org` is retired, so a multi-org user (and
 * a platform admin, who has no default org at all) picks WayAI-side; the value is fed to
 * `resolveOrgFromRequest(auth, explicitOrgId)` and only the grant-validated result is
 * attested. Stripped before forwarding — it is WayAI's selector, not Rekor's
 * input.
 */
export const DATA_PROXY_ORG_QUERY_PARAM = 'org_id';

/**
 * The request-body key carrying the inline entitlement projection on the two
 * provisioning operations. WayAI authors it; Rekor's ATTESTED
 * entrypoint middleware is the only thing allowed to read it, and persists what arrives.
 *
 * ⚠️ It is an attested-only input, like the `X-Rekor-*` headers above and for the same
 * reason: one route tree is mounted on both entrypoints, so a handler reading this key
 * would read it on public traffic too — and a `rec_` caller would then author its own
 * `ops_tenths_included` / `max_records` / `production_bases` into the key declared
 * WayAI-only. That corruption outlives detection, since staleness never fires while
 * a forged `window_resets_at` sits in the future. The Rekor side pins the read to its
 * attested middleware (`rekor:workers/backend/src/middleware/attested-projection.ts`,
 * rekor#894) under its own structural guard; this constant is WayAI's half of the same
 * literal, so the two sides cannot drift apart on a bare string.
 */
export const REKOR_INLINE_PROJECTION_BODY_KEY = 'entitlement';

/**
 * Cap on a buffered `/api/data` request body. THREE shapes are buffered, for TWO different
 * reasons; everything else on the hop streams through with no `/api/*` payload limit, so
 * this bound belongs to these alone.
 *
 * 1. A base upsert and 2. a token mint — buffered because the proxy has to ATTACH the field
 *    above (`REKOR_INLINE_PROJECTION_BODY_KEY`) to them.
 * 3. `POST /v1/:base_id/promote` — buffered to READ `source_base_id` out of it, which the
 *    post-promote credential relink needs. Nothing is attached: the body is forwarded
 *    verbatim. The name `readProvisioningBody` predates this third caller and describes
 *    where the bounded-read machinery lives, not what promote is.
 *
 * All three share this one cap deliberately: it is a memory bound on the isolate, not a
 * per-shape business rule, and a promote body is a small JSON selector nowhere near it.
 *
 * Declared here rather than backend-side for the same reason `MAX_MESSAGE_BODY_BYTES` and
 * `MAX_CI_CONFIG_BODY_BYTES` are: the producer is the CLI (`wayai bases create`,
 * `wayai bases tokens create`, `wayai bases promote`), which cannot import the backend and
 * would otherwise discover the boundary only by round-tripping the body.
 */
export const MAX_PROVISIONING_BODY_BYTES = 256 * 1024;

export const dataProxyQuery = z.object({
  [DATA_PROXY_ORG_QUERY_PARAM]: z.string().uuid().optional(),
});

/**
 * The derived attestation, as a plain header bag, and the SOLE authority on which headers
 * are required: everything but `X-Rekor-Tool-Id` is, and a request missing one is 401 on
 * the Rekor side rather than a fallthrough. Modeled as a schema rather than a bare
 * interface so a test can assert the built headers parse — that rule is the whole reason
 * the Rekor side may skip verification.
 */
export const rekorAttestation = z.object({
  [REKOR_ACTOR_TYPE_HEADER]: z.enum(['user', 'token']),
  [REKOR_ACTOR_ID_HEADER]: z.string().min(1),
  [REKOR_ACTOR_LABEL_HEADER]: z.string(),
  [REKOR_ACTOR_ORG_HEADER]: z.string().uuid(),
  [REKOR_ACTOR_ADMIN_HEADER]: z.enum(['0', '1']),
  [REKOR_CLIENT_HEADER]: rekorClientSchema,
  [REKOR_TOOL_ID_HEADER]: rekorToolIdSchema.optional(),
});

export type RekorAttestation = z.infer<typeof rekorAttestation>;

/**
 * Derived from the schema, never re-listed beside it — a hand-kept copy would let the two
 * disagree about what "required" means, which is the one property standing between a
 * malformed attestation and Rekor's `system`-actor fallback.
 */
export const REQUIRED_REKOR_ATTESTATION_HEADERS = Object.entries(rekorAttestation.shape)
  .filter(([, field]) => !field.isOptional())
  // `Object.entries` widens the keys to `string`; they are the schema's own keys by
  // construction, so narrowing them back is sound rather than an escape hatch.
  .map(([header]) => header) as readonly (keyof RekorAttestation)[];

// =============================================
// A toolset binding (#5083 RK3, decisions 9 and 10)
// =============================================

/**
 * The base and the toolset slug an attested call is confined to, sent together or not at all.
 * The far side then confines the call exactly as it confines a `rec_` token bound to that toolset:
 * that toolset's resolve, tool definitions and calls, and nothing else. A binding only narrows what
 * the attested org grants. Cross-repo wire literals: the far side spells them `BOUND_BASE_HEADER`
 * and `BOUND_TOOLSET_HEADER` in `apps/rekor/backend/src/lib/actor-headers.ts`.
 */
export const REKOR_BOUND_BASE_HEADER = 'X-Rekor-Bound-Base';
export const REKOR_BOUND_TOOLSET_HEADER = 'X-Rekor-Bound-Toolset';

/**
 * The id of the toolset token a binding was resolved from, sent beside the binding (owner decision 41
 * on wayai-pro/wayai#5083). The far side then refuses the binding of a token revoked or expired since,
 * or bound elsewhere, with a 401, as it refuses the token itself, and of a token bound to persons (#5473
 * 2-tokens), which serves only its own calls, with a 403; a binding that names no token is
 * served as before. Server-derived, hence `X-Rekor-*`; the far side spells it `BOUND_TOKEN_HEADER` in
 * `apps/rekor/backend/src/lib/actor-headers.ts`.
 */
export const REKOR_BOUND_TOKEN_HEADER = 'X-Rekor-Bound-Token';

/**
 * The customer a bound call serves (#5083 RK2): a WayAI contact id — the contact an eval run's scenario declares,
 * and nothing else: an agent's other calls (#5473 2b1) and a customer page's (2b2) name none — which keys its
 * customer record in the base under the reserved `wayai_contact` source (owner
 * decision 20). Sent beside the binding, and only to a customer-scoped toolset: such a toolset
 * refuses a call without one, and any other toolset refuses a call with one. Server-derived, hence
 * `X-Rekor-*`; the far side spells it `BOUND_CUSTOMER_HEADER` in
 * `apps/rekor/backend/src/lib/actor-headers.ts`.
 */
export const REKOR_BOUND_CUSTOMER_HEADER = 'X-Rekor-Bound-Customer';

/**
 * The access set a bound tool call carries (#5358): per access dimension, the value ids the call may
 * read and may write, as compact JSON (`{"class":{"read":["7A","7B"],"write":["7A"]}}`), `["*"]`
 * reaching a dimension unrestricted and a dimension left out reaching nothing. Its shape is
 * `accessSetSchema` (`setup/access-dimensions.ts`), held to the caps below. Sent beside the binding,
 * over the `REKOR` binding only. Server-derived, hence `X-Rekor-*`; the far side spells it
 * `BOUND_ACCESS_HEADER` in `apps/rekor/backend/src/lib/actor-headers.ts`.
 */
export const REKOR_BOUND_ACCESS_HEADER = 'X-Rekor-Bound-Access';

/**
 * Where a bound tool call's access set applies, when not on every record type (#5358): its one value,
 * {@link REKOR_ACCESS_SCOPE_CUSTOMER}, marks the set customer-only, and the call customer-facing (#5473 §4).
 * On a call naming its customer (`REKOR_BOUND_CUSTOMER_HEADER`), the far side holds the set only on the
 * record types customer scope narrows to one customer (`owned`, `root`), and every other record type that
 * enforces an access dimension reaches nothing. On a call naming none (#5473 §9), it holds the set on every
 * record type, beside the audience gate. WayAI marks a turn's set this way when a party of the turn is everyone
 * else, whose values are the hub's public values (`apps/backend/src/rekor/access-set.ts`), and, on a call naming
 * no customer, whenever the persons it names are customer-facing (#5473 §9, 2b1, `buildToolsetAttestation`): the
 * real set beside them, which a far side from before #5473 2a reads as `{}`. Accepted only beside
 * `REKOR_BOUND_ACCESS_HEADER`: alone, or with any other value, it is a 401 there. Server-derived, hence
 * `X-Rekor-*`; the far side spells it `BOUND_ACCESS_SCOPE_HEADER` in
 * `apps/rekor/backend/src/lib/actor-headers.ts`.
 */
export const REKOR_BOUND_ACCESS_SCOPE_HEADER = 'X-Rekor-Bound-Access-Scope';

/**
 * The hub whose page makes a bound call (wayai-pro/wayai#5333): its id, sent beside the binding on
 * every call a hub page makes through WayAI's page bridge, and on no other. The far side recognizes a
 * page's call by this header alone, never by the actor id's shape (an agent's id is admin-choosable),
 * and counts it in the hub's own page bucket (`page:<org_id>:<hub_id>`) rather than the org's agent
 * bucket. The actor, a team page's signed-in viewer (#5333 Phase 2), is the writer only. Server-derived, hence
 * `X-Rekor-*`; the far side spells it `BOUND_PAGE_HEADER` in `apps/rekor/backend/src/lib/actor-headers.ts`.
 */
export const REKOR_BOUND_PAGE_HEADER = 'X-Rekor-Bound-Page';

/**
 * The persons a bound call is for (wayai-pro/wayai#5473 §9): compact JSON
 * `{"audience":"staff"|"customer","parties":[[ids…] | "*", …],"agent_all":boolean,"customer"?:index}`, its shape
 * `rekorBoundPersonSchema` (`rekor-toolset.ts`), held to the caps below. Each party is one person-bound
 * party of the turn: the person ids of its person set (empty when unproven), or `"*"` (it reaches every
 * person). `agent_all` is the agent's own `"*"`, which applies only beside the `staff` audience. `customer`,
 * optional (#5473 2d1), is the index of the conversation's customer party, never beside the `staff` audience nor
 * at a `"*"` party; a create the call makes fills an omitted `owner` field with that party's primary id. A sibling of
 * `REKOR_BOUND_ACCESS_HEADER`, whose JSON is a flat map of dimensions. Sent beside the binding, over the
 * `REKOR` binding only. Server-derived, hence `X-Rekor-*`; the far side spells it `BOUND_PERSON_HEADER` in
 * `apps/rekor/backend/src/lib/actor-headers.ts`, reads it and carries it on the binding, and holds a call's
 * reads and writes of a person-ruled record type to it, whatever its audience. A customer-facing call — any
 * audience but `staff`, or a bound call naming no persons — it also holds to the audience gate (#5473 §4): it
 * reaches only person-ruled and `{ shared: true }` record types.
 */
export const REKOR_BOUND_PERSON_HEADER = 'X-Rekor-Bound-Person';

/**
 * The audience `REKOR_BOUND_PERSON_HEADER` names (#5473 §4): `staff` only when the turn is staff's, and
 * `customer` for a customer-facing call. The far side reads any audience but `staff` (none included) as
 * customer-facing. It spells them `PERSON_AUDIENCE_STAFF` and `PERSON_AUDIENCE_CUSTOMER` in
 * `apps/rekor/shared/src/types/api.ts`.
 */
export const REKOR_PERSON_AUDIENCE_STAFF = 'staff';
export const REKOR_PERSON_AUDIENCE_CUSTOMER = 'customer';

/**
 * A party of `REKOR_BOUND_PERSON_HEADER` that reaches every person (#5473 §3: `person: ["*"]`): the whole
 * party, never an id beside others. The far side spells it `PERSON_ALL` in `apps/rekor/shared/src/types/api.ts`.
 */
export const REKOR_PERSON_ALL = '*';

/**
 * The one value `REKOR_BOUND_ACCESS_SCOPE_HEADER` takes: a customer-only access set. The far side spells
 * it `ACCESS_SCOPE_CUSTOMER` in `apps/rekor/shared/src/types/api.ts`.
 */
export const REKOR_ACCESS_SCOPE_CUSTOMER = 'customer';

/** Where a bound tool call's access set applies, when not on every record type: customer-only. */
export type RekorAccessScope = typeof REKOR_ACCESS_SCOPE_CUSTOMER;

/**
 * The base incarnation a request states. Required beside a binding (decision 10): without it, a
 * binding would follow a recycled base id to whatever base now holds it. A stale one is refused
 * with a 409 whose code is `REKOR_BASE_INCARNATION_MISMATCH` (`rekor-toolset.ts`). An
 * integrator-read header, hence `X-Data-*`; the far side spells it `DATA_BASE_GENERATION_HEADER` in
 * `apps/rekor/shared/src/constants.ts`.
 */
export const DATA_BASE_GENERATION_HEADER = 'X-Data-Base-Generation';

/**
 * The data product's caps on an attested actor's id and label (`MAX_ACTOR_ID_LEN` and
 * `MAX_ACTOR_LABEL_LEN` in its attested-auth middleware): a longer one is a 401 there, never
 * truncated.
 */
export const REKOR_MAX_ACTOR_ID_LENGTH = 256;
export const REKOR_MAX_ACTOR_LABEL_LENGTH = 256;

/**
 * The data product's caps on the access set a bound call carries (#5358; `MAX_ACCESS_DIMENSIONS`,
 * `MAX_ACCESS_VALUES`, `MAX_ACCESS_ID_LEN` and `MAX_ACCESS_HEADER_LEN` in its attested-auth
 * middleware): the dimensions one set names, the value ids one dimension's `read` or `write` list
 * holds, the length of a dimension or value id, and the header's own length. A set past any of them is
 * a 401 there. WayAI holds an org's access dimensions and values to the first three
 * (`setup/access-dimensions.ts`), and its id charset is one JSON never escapes, so a set at all three
 * caps at once is within the fourth (pinned in `setup/access-dimensions.test.ts`).
 */
export const REKOR_MAX_ACCESS_DIMENSIONS = 8;
export const REKOR_MAX_ACCESS_VALUES = 100;
export const REKOR_MAX_ACCESS_ID_LENGTH = 32;
export const REKOR_MAX_ACCESS_HEADER_LENGTH = 65536;

/**
 * The data product's caps on the persons a bound call is for (#5473 §9; `MAX_PERSON_PARTIES`,
 * `MAX_PERSON_IDS`, `MAX_PERSON_ID_LEN` and `MAX_PERSON_HEADER_LEN` in its attested-auth middleware): the
 * parties one header names, the person ids one party holds, the length of a person id, and the header's own
 * length. A header past any of them is a 401 there.
 *
 * - Ids per party: a person set holds at most 16 ids (owner decision D5 on #5473); a party over it fails
 *   closed WayAI-side rather than being sent.
 * - Parties: a turn names at most two person-bound parties today (`turnAccessOf`: its requester, and the
 *   team member sending or a consult initiator); a delegated task carries every person-bound party of the
 *   call that delegated it, and a delegation chain at most `MAX_DELEGATION_ORIGINATORS` originators
 *   (`apps/backend/src/rekor/access-set.ts`). The cap leaves room for both, with every hop's own parties and
 *   an eval's declared persons, while bounding the header within the last cap.
 * - Id length: a person id (`prs_` and a base32 HMAC) and a contact id (a UUID) both fit.
 * - Header: a header at the first three caps at once, written as compact JSON with ids in the structural
 *   charset (`REKOR_STRUCTURAL_ID_PATTERN`, which JSON never escapes), is within it (pinned in
 *   `rekor-toolset.test.ts`).
 */
export const REKOR_MAX_PERSON_PARTIES = 16;
export const REKOR_MAX_PERSON_IDS = 16;
export const REKOR_MAX_PERSON_ID_LENGTH = 64;
export const REKOR_MAX_PERSON_HEADER_LENGTH = 32768;
